import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { App } from "./app/App";
import { ConvexSetupProvider } from "./convex/ConvexSetupProvider";
import { registerShellServiceWorker } from "./pwa/registerServiceWorker";

import type { DevPersonaHarness } from "./dev/devHarness";

const container = document.getElementById("root");
if (!container) {
  throw new Error("Root container #root is missing from index.html");
}

const configuredConvexUrl: unknown = import.meta.env["VITE_CONVEX_URL"];
const convexUrl =
  typeof configuredConvexUrl === "string" && configuredConvexUrl.length > 0
    ? configuredConvexUrl
    : undefined;
const configuredClerkPublishableKey: unknown = import.meta.env["VITE_CLERK_PUBLISHABLE_KEY"];
const clerkPublishableKey =
  typeof configuredClerkPublishableKey === "string" && configuredClerkPublishableKey.length > 0
    ? configuredClerkPublishableKey
    : undefined;

const root = createRoot(container);

function renderPersona(harness: DevPersonaHarness | null): void {
  const identityClient = harness?.session ?? null;
  const live = Boolean(convexUrl);
  const app = <App identityClient={identityClient} live={live} />;

  root.render(
    <StrictMode>
      {convexUrl === undefined ? (
        app
      ) : (
        <ConvexSetupProvider convexUrl={convexUrl} identityClient={identityClient}>
          {app}
        </ConvexSetupProvider>
      )}
      {harness === null ? undefined : <harness.Bar />}
    </StrictMode>,
  );
}

function renderClerk(): void {
  void import("./identity/ClerkIdentityApp").then(({ ClerkIdentityApp }) => {
    root.render(
      <StrictMode>
        <ClerkIdentityApp convexUrl={convexUrl} publishableKey={clerkPublishableKey ?? ""} />
      </StrictMode>,
    );
  });
}

// THE DEV-ONLY BRANCH (bead hf-e3-convex-boundary-zf7.4.2).
//
// `import.meta.env.DEV` is replaced with a literal `false` by Vite in a production
// build, so Rollup deletes this branch and the dynamic import inside it — which is
// the only edge into src/dev/. Nothing under that directory reaches `dist`, and
// src/dev/prodBundle.test.ts proves it by building for real rather than by trusting
// this comment.
//
// The type-only import of DevPersonaHarness above erases at compile time and creates
// no such edge, which is why it is written `import type`.
if (clerkPublishableKey !== undefined) {
  renderClerk();
} else if (import.meta.env.DEV) {
  void import("./dev/devHarness").then(
    (module) => {
      renderPersona(module.createDevPersonaHarness());
    },
    () => {
      // The switcher failing to load must never cost the app its render.
      renderPersona(null);
    },
  );
} else {
  renderPersona(null);
}

registerShellServiceWorker();
